// Code-action: "Add missing table to FROM" — Trigger / candidate selection.
//
// Split out of addMissingTableToFrom.ts (behavior-neutral).

import type { DottedPart } from '../../../sql-resolver/dotted/dottedParts';
import type { DatabaseExplorerNode } from '../../../explorer/models/DatabaseTreeModels';
import type { CodeActionContext } from '../../types';
import type { Candidate, ScopeRelation } from './addMissingTableToFrom.types';
import {
  findTableByName,
  findTablesWithColumn,
  relationOwnsColumn,
  tableOwnsColumn,
} from './addMissingTableToFrom.tree';
import { collectFkEdges } from './addMissingTableToFrom.fk';
import { isInScope } from './addMissingTableToFrom.scope';

export function collectCandidates(
  parts: readonly DottedPart[],
  treeData: readonly DatabaseExplorerNode[],
  engineId: CodeActionContext['engineId'],
  connectionId: string,
  database: string | null,
  inScope: readonly ScopeRelation[],
  defaultSchema: string,
): readonly Candidate[] {
  if (parts.length === 1) {
    const colFolded = parts[0].folded.toLowerCase();
    return collectBareColumnCandidates(
      treeData,
      engineId,
      connectionId,
      database,
      inScope,
      colFolded,
      defaultSchema,
      /* requireFk = */ true,
    );
  }
  // 2-part: prefix.column.
  const prefix = parts[0].folded.toLowerCase();
  const prefixSourceText = parts[0].value;
  const colFolded = parts[1].folded.toLowerCase();

  // Skip if prefix already matches an in-scope alias / unaliased relation —
  // that's the resolver's territory (column either resolves or is ambiguous).
  if (inScope.some((r) => r.displayRef.toLowerCase() === prefix || r.name.toLowerCase() === prefix)) {
    return [];
  }

  // a) Real table named `prefix` with column `column`.
  const direct = findTableByName(treeData, connectionId, database, prefix, defaultSchema);
  if (direct && tableOwnsColumn(direct.node, colFolded)) {
    return [{
      tableName: direct.name,
      schema: direct.schema,
      orderKey: direct.name.toLowerCase(),
    }];
  }
  if (direct) {
    // Real table by that name exists but doesn't own `column`. Don't fall
    // through to alias-matching — the user clearly meant *that* table; the
    // missing column is a separate problem.
    return [];
  }

  // b) Tables that own `column` — surface them all as alias-using candidates.
  const owners = findTablesWithColumn(
    treeData,
    connectionId,
    database,
    colFolded,
    defaultSchema,
  );
  return owners
    .filter((t) => !isInScope(t.name, t.schema, inScope))
    .map<Candidate>((t) => ({
      tableName: t.name,
      schema: t.schema,
      aliasText: prefixSourceText,
      orderKey: t.name.toLowerCase(),
    }));
}

function collectBareColumnCandidates(
  treeData: readonly DatabaseExplorerNode[],
  engineId: CodeActionContext['engineId'],
  connectionId: string,
  database: string | null,
  inScope: readonly ScopeRelation[],
  colFolded: string,
  defaultSchema: string,
  requireFk: boolean,
): readonly Candidate[] {
  // If any in-scope relation already owns the column, the resolver can pin
  // it — this fix isn't applicable.
  if (
    inScope.some((r) =>
      relationOwnsColumn(treeData, connectionId, database, r.name, r.schema, colFolded),
    )
  ) {
    return [];
  }

  const owners = findTablesWithColumn(
    treeData,
    connectionId,
    database,
    colFolded,
    defaultSchema,
  );
  const offScope = owners.filter((t) => !isInScope(t.name, t.schema, inScope));
  if (!requireFk) {
    return offScope.map((t) => ({
      tableName: t.name,
      schema: t.schema,
      orderKey: t.name.toLowerCase(),
    }));
  }
  // Bare column: keep the menu signal-to-noise high by surfacing only
  // FK-having candidates.
  return offScope
    .filter((t) =>
      collectFkEdges(
        treeData,
        engineId,
        connectionId,
        database,
        { tableName: t.name, schema: t.schema, orderKey: '' },
        inScope,
        defaultSchema,
      ).length > 0,
    )
    .map<Candidate>((t) => ({
      tableName: t.name,
      schema: t.schema,
      orderKey: t.name.toLowerCase(),
    }));
}
